import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClockCounterClockwise, ShieldStar } from '@phosphor-icons/react';

const ACTION_LABELS = {
    list_users: 'Listed all users',
    view_user: 'Viewed user',
    delete_user: 'Deleted user',
    impersonate_start: 'Started impersonating',
    impersonate_end: 'Ended impersonation',
};

const ACTION_CLS = {
    delete_user: 'bg-red-50 text-red-800',
    impersonate_start: 'bg-amber-50 text-amber-900',
    impersonate_end: 'bg-slate-100 text-slate-700',
    view_user: 'bg-slate-100 text-slate-700',
    list_users: 'bg-slate-100 text-slate-700',
};

export default function AdminAudit() {
    const { data: items = [], isLoading } = useQuery({
        queryKey: ['admin-audit'],
        queryFn: async () => (await api.get('/admin/audit', { params: { limit: 500 } })).data,
    });

    return (
        <div className="px-6 md:px-10 py-8 max-w-5xl" data-testid="admin-audit-root">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
                <ShieldStar size={12} weight="duotone" /> Tanow staff
            </div>
            <header className="mb-6 flex items-end justify-between">
                <div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 flex items-center gap-3">
                        <ClockCounterClockwise size={30} className="text-primary" /> Audit log
                    </h1>
                    <p className="text-slate-600 mt-2">Every admin view, impersonation, and delete. Ordered newest first.</p>
                </div>
                <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                    <ArrowLeft size={14} /> All users
                </Link>
            </header>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="grid grid-cols-[170px_140px_1fr_1fr] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>When</div><div>Action</div><div>Admin</div><div>Target</div>
                </div>
                {isLoading ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">No admin activity yet.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {items.map(a => (
                            <div key={a.audit_id} data-testid="admin-audit-row" className="grid grid-cols-[170px_140px_1fr_1fr] px-5 py-3 items-center text-sm">
                                <div className="text-slate-500">{new Date(a.at).toLocaleString()}</div>
                                <div>
                                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${ACTION_CLS[a.action] || 'bg-slate-100 text-slate-700'}`}>
                                        {ACTION_LABELS[a.action] || a.action}
                                    </span>
                                </div>
                                <div className="text-slate-800 truncate">
                                    <div className="text-slate-900">{a.admin?.name || a.admin_email}</div>
                                    <div className="text-[11px] text-slate-500 truncate">{a.admin_email}</div>
                                </div>
                                <div className="text-slate-800 truncate">
                                    {a.target_user ? (
                                        <>
                                            <div className="text-slate-900">{a.target_user.name || a.target_user.email}</div>
                                            <div className="text-[11px] text-slate-500 truncate">{a.target_user.email}</div>
                                        </>
                                    ) : a.target_user_id ? (
                                        <span className="text-slate-500 font-mono text-xs">{a.target_user_id}</span>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                    {a.metadata && Object.keys(a.metadata).length > 0 && (
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {Object.entries(a.metadata).map(([k, v]) => (
                                                <span key={k} className="mr-2">{k}: <b>{String(v)}</b></span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
